import React, { useState, useEffect, useRef } from 'react';
import { Camera, Plus, X, Calendar, ChevronLeft, ChevronRight, Download, Printer, Trash2 } from 'lucide-react';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PhotoCalendar = () => {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [monthData, setMonthData] = useState({});
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [eventForm, setEventForm] = useState({ date: '', title: '', type: 'birthday' });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showClearModal, setShowClearModal] = useState(false);
  const calendarRef = useRef(null);

  // Load data from localStorage
  useEffect(() => {
    setLoading(true);
    try {
      const savedData = localStorage.getItem(`calendar-${year}`);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setMonthData(parsed);
      } else {
        setMonthData({});
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMonthData({});
    }
    setLoading(false);
  }, [year]);

  // Save data to localStorage
  useEffect(() => {
    if (!loading) {
      try {
        localStorage.setItem(`calendar-${year}`, JSON.stringify(monthData));
      } catch (error) {
        console.error('Error saving data:', error);
        alert('Error saving data. Storage might be full.');
      }
    }
  }, [monthData, year, loading]);

  // Form validation
  const validateEventForm = () => {
    const newErrors = {};
    
    if (!eventForm.date) {
      newErrors.date = 'Date is required';
    } else {
      const dateNum = parseInt(eventForm.date);
      const maxDays = getDaysInMonth(selectedMonth);
      if (dateNum < 1 || dateNum > maxDays) {
        newErrors.date = `Date must be between 1 and ${maxDays}`;
      }
    }
    
    if (!eventForm.title || eventForm.title.trim() === '') {
      newErrors.title = 'Title is required';
    } else if (eventForm.title.trim().length < 2) {
      newErrors.title = 'Title must be at least 2 characters';
    } else if (eventForm.title.length > 50) {
      newErrors.title = 'Title must be less than 50 characters';
    }
    
    if (!eventForm.type) {
      newErrors.type = 'Event type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = (month, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image size should be less than 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setMonthData(prev => ({
        ...prev,
        [month]: {
          ...prev[month],
          image: reader.result,
          imageName: file.name,
          uploadDate: new Date().toISOString()
        }
      }));
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (month) => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      setMonthData(prev => ({
        ...prev,
        [month]: {
          ...prev[month],
          image: null,
          imageName: null,
          uploadDate: null
        }
      }));
    }
  };

  const addEvent = () => {
    if (!validateEventForm()) {
      return;
    }

    const newEvent = {
      ...eventForm,
      date: parseInt(eventForm.date),
      title: eventForm.title.trim(),
      id: Date.now(),
      createdAt: new Date().toISOString()
    };

    setMonthData(prev => ({
      ...prev,
      [selectedMonth]: {
        ...prev[selectedMonth],
        events: [
          ...(prev[selectedMonth]?.events || []),
          newEvent
        ]
      }
    }));
    
    setEventForm({ date: '', title: '', type: 'birthday' });
    setErrors({});
    setShowEventModal(false);
  };

  const removeEvent = (month, eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      setMonthData(prev => ({
        ...prev,
        [month]: {
          ...prev[month],
          events: prev[month].events.filter(e => e.id !== eventId)
        }
      }));
    }
  };

  const clearAllData = () => {
    if (window.confirm('⚠️ This will delete ALL photos and events for this year. Are you absolutely sure?')) {
      try {
        localStorage.removeItem(`calendar-${year}`);
        setMonthData({});
        setShowClearModal(false);
        alert('✅ All data cleared successfully!');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please try again.');
      }
    }
  };

  const exportData = () => {
    try {
      const dataStr = JSON.stringify(monthData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calendar-backup-${year}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  const importData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (window.confirm('This will replace current data. Continue?')) {
          setMonthData(imported);
          alert('✅ Data imported successfully!');
        }
      } catch (error) {
        console.error('Import error:', error);
        alert('Invalid file format. Please upload a valid backup file.');
      }
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };
    reader.readAsText(file);
  };

  // Print functionality
  const handlePrint = () => {
    window.print();
  };

  // Download as image functionality
  const handleDownload = async () => {
    try {
      const element = calendarRef.current;
      
      if (window.html2canvas) {
        captureAndDownload(element);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => {
          captureAndDownload(element);
        };
        script.onerror = () => {
          alert('Error loading download library. Please try again.');
        };
        document.head.appendChild(script);
      }
    } catch (error) {
      console.error('Error downloading calendar:', error);
      alert('Error downloading calendar. Please try again.');
    }
  };

  const captureAndDownload = (element) => {
    window.html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#f9fafb'
    }).then(canvas => {
      const link = document.createElement('a');
      link.download = `calendar-${year}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }).catch(err => {
      console.error('Capture error:', err);
      alert('Error capturing calendar. Please try again.');
    });
  };

  const getDaysInMonth = (month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendarGrid = (month) => {
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];
    const events = monthData[month]?.events || [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 sm:h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = events.filter(e => parseInt(e.date) === day);
      const isToday = year === currentDate.getFullYear() && 
                      month === currentDate.getMonth() && 
                      day === currentDate.getDate();
      
      days.push(
        <div 
          key={day} 
          className={`h-8 sm:h-10 relative border rounded flex items-center justify-center text-xs sm:text-sm hover:bg-gray-50 transition-colors ${
            isToday ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'
          }`}
        >
          <span className={`font-medium ${isToday ? 'text-blue-600 font-bold' : ''}`}>{day}</span>
          {dayEvents.length > 0 && (
            <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 flex gap-0.5">
              {dayEvents.slice(0, 3).map((evt, i) => (
                <div
                  key={i}
                  className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${
                    evt.type === 'birthday' ? 'bg-pink-500' :
                    evt.type === 'anniversary' ? 'bg-red-500' :
                    'bg-blue-500'
                  }`}
                  title={evt.title}
                />
              ))}
              {dayEvents.length > 3 && (
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gray-400" title={`+${dayEvents.length - 3} more`} />
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const MonthCard = ({ month }) => {
    const monthImg = monthData[month]?.image;
    const events = monthData[month]?.events || [];
    const isCurrentMonth = year === currentDate.getFullYear() && month === currentDate.getMonth();

    return (
      <div className={`bg-white rounded-lg shadow-md overflow-hidden transition-all hover:shadow-lg flex flex-col h-full ${
        isCurrentMonth ? 'ring-2 ring-blue-500' : ''
      }`}>
        
        <div className="relative h-40 sm:h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0">
          {monthImg ? (
            <>
              <img src={monthImg} alt={monthNames[month]} className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(month)}
                className="absolute top-2 left-2 bg-red-500 text-white p-1.5 sm:p-2 rounded-full shadow hover:bg-red-600 no-print transition-colors"
                title="Remove Image"
              >
                <X className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <Camera className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mb-2" />
              <span className="text-xs text-gray-500">No photo uploaded</span>
            </div>
          )}
          <label className="absolute top-2 right-2 bg-white p-1.5 sm:p-2 rounded-full shadow cursor-pointer hover:bg-gray-50 no-print transition-colors">
            <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={(e) => handleImageUpload(month, e)}
              className="hidden"
            />
          </label>
        </div>
        
        <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between">
          <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">{monthNames[month]}</h3>
          
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 sm:mb-3 text-xs text-gray-600 flex-shrink-0">
            <div className="text-center font-medium">S</div>
            <div className="text-center font-medium">M</div>
            <div className="text-center font-medium">T</div>
            <div className="text-center font-medium">W</div>
            <div className="text-center font-medium">T</div>
            <div className="text-center font-medium">F</div>
            <div className="text-center font-medium">S</div>
            {renderCalendarGrid(month)}
          </div>

          <button
            onClick={() => {
              setSelectedMonth(month);
              setEventForm({ date: '', title: '', type: 'birthday' });
              setErrors({});
              setShowEventModal(true);
            }}
            className="w-full py-1.5 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-2 text-xs sm:text-sm no-print transition-colors flex-shrink-0"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
            Add Event
          </button>

          {events.length > 0 && (
            <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2 max-h-24 sm:max-h-32 overflow-y-auto flex-shrink-0">
              {events.sort((a, b) => a.date - b.date).map(event => (
                <div
                  key={event.id}
                  className="flex items-center justify-between text-xs bg-gray-50 p-1.5 sm:p-2 rounded hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                    <div
                      className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${
                        event.type === 'birthday' ? 'bg-pink-500' :
                        event.type === 'anniversary' ? 'bg-red-500' :
                        'bg-blue-500'
                      }`}
                    />
                    <span className="font-medium">{monthNames[month]} {event.date}</span>
                    <span className="text-gray-600 truncate">- {event.title}</span>
                  </div>
                  <button
                    onClick={() => removeEvent(month, event.id)}
                    className="text-red-500 hover:text-red-700 no-print flex-shrink-0 ml-1 sm:ml-2"
                    title="Delete Event"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <style>
        {`
          @media print {
            body { 
              margin: 0;
              background: white;
            }
            .no-print { 
              display: none !important; 
            }
            .print-container { 
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 1rem;
            }
            @page {
              size: landscape;
              margin: 1cm;
            }
          }
        `}
      </style>
      
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6 no-print">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
              <h1 className="text-xl sm:text-3xl font-bold">Photo Calendar {year}</h1>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs sm:text-sm"
                title="Download as Image"
              >
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Download</span>
              </button>
              
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-xs sm:text-sm"
                title="Print Calendar"
              >
                <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Print</span>
              </button>

              <button
                onClick={exportData}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors text-xs sm:text-sm"
                title="Export Backup"
              >
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Backup</span>
              </button>

              <label className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors text-xs sm:text-sm cursor-pointer">
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Import</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={importData}
                  className="hidden"
                />
              </label>

              <button
                onClick={() => setShowClearModal(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-xs sm:text-sm"
                title="Clear All Data"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Clear</span>
              </button>
              
              <div className="flex items-center gap-2 ml-0 sm:ml-2">
                <button
                  onClick={() => setYear(year - 1)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= 1900 && val <= 2100) {
                      setYear(val);
                    }
                  }}
                  min="1900"
                  max="2100"
                  className="w-16 sm:w-20 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded px-1 sm:px-2 py-0.5 sm:py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => setYear(year + 1)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 flex flex-wrap gap-3 sm:gap-4 text-xs">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-pink-500" />
              <span>Birthday</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" />
              <span>Anniversary</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500" />
              <span>Other Event</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
              <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-blue-500 bg-blue-50 rounded" />
              <span>{new Date().toLocaleDateString()} Today's Date</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-sm sm:text-base text-gray-600">Loading calendar...</p>
          </div>
        ) : (
          <div ref={calendarRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 print-container auto-rows-fr">
            {monthNames.map((_, index) => (
              <MonthCard key={index} month={index} />
            ))}
          </div>
        )}
      </div>

      {/* Add Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">Add Event - {monthNames[selectedMonth]}</h2>
              <button
                onClick={() => {
                  setShowEventModal(false);
                  setEventForm({ date: '', title: '', type: 'birthday' });
                  setErrors({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max={getDaysInMonth(selectedMonth)}
                  value={eventForm.date}
                  onChange={(e) => {
                    setEventForm({ ...eventForm, date: e.target.value });
                    if (errors.date) setErrors({ ...errors, date: null });
                  }}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
                    errors.date ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder={`Day (1-${getDaysInMonth(selectedMonth)})`}
                />
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => {
                    setEventForm({ ...eventForm, title: e.target.value });
                    if (errors.title) setErrors({ ...errors, title: null });
                  }}
                  maxLength="50"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
                    errors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Event name (2-50 characters)"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                <p className="text-gray-500 text-xs mt-1">{eventForm.title.length}/50 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={eventForm.type}
                  onChange={(e) => {
                    setEventForm({ ...eventForm, type: e.target.value });
                    if (errors.type) setErrors({ ...errors, type: null });
                  }}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
                    errors.type ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                >
                  <option value="birthday">🎂 Birthday</option>
                  <option value="anniversary">❤️ Anniversary</option>
                  <option value="other">📅 Other</option>
                </select>
                {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
              </div>

              <button
                onClick={addEvent}
                className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 font-medium transition-colors"
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Data Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-red-600">⚠️ Warning</h2>
              <button
                onClick={() => setShowClearModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
              This will permanently delete <strong>ALL photos and events</strong> for the year {year}. 
              This action cannot be undone.
            </p>

            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
              💡 Tip: Use the "Backup" button to export your data before clearing.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowClearModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={clearAllData}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm sm:text-base"
              >
                Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="text-center text-xs text-gray-500 mt-8 mb-4 no-print">
        &copy; {new Date().getFullYear()} Photos Calendar. Made with ❤️ by Kuldeep Kumawat.
      </footer>
    </div>
  );
};

export default PhotoCalendar;